<template>
  <div class="fill-height new-talents-page" v-scroll="onScroll">

    <div class="background-filler" />

    <transition appear name="player">
      <div class="player-view" :class="{'player-view--full': !selectedTalent, 'player-view--restricted': selectedTalent}" v-if="!showIntro && $vuetify.breakpoint.mdAndUp">
        <ImagePlayer
          class="image-player"
          ref="player"
          :frames="playerFrames"
          :currentFrame.sync="playerCurentFrame"
          :loading.sync="playerLoading"
          :fps="30"
          :load-progress.sync="playerLoadProgress"
        />
      </div>
    </transition>

    <transition>
      <seo-v-img cover :key="mobileTalentCentered" class="mobile-preview" v-if="mobileTalentCentered != -1 && talentsList && $vuetify.breakpoint.smAndDown && !selectedTalent" :src="`/media/${talentsList[mobileTalentCentered].mobilePreview.id}`" :lazy-src="JSON.parse(talentsList[mobileTalentCentered].mobilePreview.metaData)['image-lazy-src']" />
    </transition>

    <transition name="talent-menu">
      <!-- desktop menu on left -->
      <!-- mobile menu on lading -->

      <div class="talent-menu" v-if="(($vuetify.breakpoint.mdAndUp && !selectedTalent && talentsList) || ($vuetify.breakpoint.smAndDown && !selectedTalent)) && !showIntro" key="open" ref="talentMenu">
        <div class="current">
          <GenericMenuItem v-for="(talent, i) in talentsList" :key="i" :to="{ params: {talentId: talent.order.toString(), talentName: talent._slug} }" :dot-offset-y="30" :data-talent-index="i" :force-active="mobileTalentCentered === i" >
            <span>{{talent.name}}</span>
            <span class="nationality">{{talent[`${$language.current}Nationality`]}}</span>
          </GenericMenuItem>
        </div>
        <div class="after">
          <GenericMenuItem v-for="(talent, i) in talentsList" :key="i" :to="{ params: {talentId: talent.order.toString(), talentName: talent._slug} }" :dot-offset-y="30" :data-talent-index="i" :force-active="mobileTalentCentered === i">
            <span>{{talent.name}}</span>
            <span class="nationality">{{talent[`${$language.current}Nationality`]}}</span>
          </GenericMenuItem>
        </div>
      </div>
      <!-- menu on left when talent is selected on desktop -->
      <div class="talent-menu-overlay" v-else-if="selectedTalent && talentsList && $vuetify.breakpoint.mdAndUp && !showIntro" key="overlay">
        <ul class="centered">
          <li data-cursor-target="left center" v-for="(talent, i) in talentsList" :key="i" :class="{active: talentId && talentId.toString() === talent.order.toString()}">
            <router-link :to="{ params: {talentId: talent.order.toString(), talentName: talent._slug} }">{{talent.name}}</router-link>
          </li>
        </ul>
      </div>
      <div v-else-if="$vuetify.breakpoint.mdAndUp" class="pt-15 talent-menu">
        <v-skeleton-loader type="list-item"/>
        <v-skeleton-loader type="list-item"/>
      </div>
    </transition>

    <transition
      name="talent-description"
      @before-enter="talentEntered = false"
      @after-enter="talentEntered = true"
      @after-leave="talentIdInt === -1 ? talentEntered = false : null"
      >
      <TalentDescription v-if="selectedTalent && !showIntro" :talent-slug="selectedTalent._slug" :player-loading="playerLoading || playerZoomingBack" :key="selectedTalent._slug" class="talent-description" />
    </transition>

    <transition>
      <v-btn aria-label="Close" v-if="selectedTalent && !showIntro" class="fixed-close-button" data-cursor-target="center center" icon exact :to="{params:{talentId: null, talentName: null}}">
        <CloseIcon class="close-icon" v-if="$vuetify.breakpoint.mdAndUp" />
        <ArrowLeft class="close-icon" v-else />
      </v-btn>
    </transition>

    <transition>
      <v-row v-if="showIntro" class="pt-12 fill-height intro" align="center" justify="center">
        <v-col cols="11" md="6" class="intro-page">
          <WorldMap class="world-map" />
          <transition appear name="from-below"><p class="mb-3"><MarkdownBlock :text="$root.siteSettings.talentsPageIntro" /></p></transition>
          <!--quando devo utilizzare il progress devo utilizzare anche la classe mask-->
          <DiscoverButton high-visibility mask :style="{'--progress': introAutoClickProgress}" @click="showIntro = false" />
        </v-col>
      </v-row>
    </transition>

  </div>
</template>

<script>
import frames from '@/assets/img/sequences/test_talents-frames'
import ImagePlayer from '@/molecules/ImagePlayer'
import ListTalent from '@/graphql/ListTalent.gql'
import TalentDescription from '@/organisms/TalentDescription'
import find from 'lodash/find'
import GenericMenuItem from '@/atoms/GenericMenuItem'
import WorldMap from '@/assets/img/world-map.svg'
import DiscoverButton from '@/atoms/DiscoverButton'
import CloseIcon from '@/assets/img/close.svg'
import ArrowLeft from '@/assets/img/chevron-left.svg'
import { MarkdownBlock, Viewport } from '@monogrid/vue-lib'
// import { RAF } from '@monogrid/js-utils'
// import LoadingSpinner from '@/atoms/LoadingSpinner'
import anime from 'animejs'
import delay from 'lodash/delay'

const scrollUpperMargin = 4

export default {
  name: 'TalentsPage',
  mixins: [Viewport],
  components: {
    CloseIcon,
    ArrowLeft,
    TalentDescription,
    ImagePlayer,
    GenericMenuItem,
    WorldMap,
    MarkdownBlock,
    DiscoverButton
    // LoadingSpinner
  },

  apollo: {
    Talents: {
      query: ListTalent,
      variables () {
        return {
          sortOn: '_slug',
          descending: false,
          status: 'PUBLISHED'
        }
      },
      result () {
        if (!this.showIntro) {
          this.$root.$emit('scroll-to-saved-position')
          delay(this.searchCenteredTalent, 200)
        }
      }
    }
  },

  props: {
    talentId: {
      type: String,
      required: false
    }
  },

  data () {
    return {
      showIntro: !this.$root.platform.prerenderer, // don't show intro to prerenderer
      introAutoClickProgress: 0,
      lastScrollPos: 0,
      talentEntered: false,
      playerCurentFrame: 0,
      playerLoading: this.$vuetify.breakpoint.mdAndUp,
      playerZoomingBack: false,
      playerLoadProgress: 0,
      mobileTalentCentered: -1,
      playerFrames: this.$root.platform.ie11 ? [frames.jpglow[0]] : window.Modernizr.webp ? [frames.webp[0]] : [frames.jpg[0]]
    }
  },
  mounted () {
    this.introAutoClickProgress = 0
    anime.remove(this)
    anime({
      targets: this,
      introAutoClickProgress: 1,
      duration: 6000,
      easing: 'linear'
    })
    // RAF.add(this.onScroll)
  },
  destroyed  () {
    // RAF.remove(this.onScroll)
  },
  watch: {
    '$vuetify.breakpoint.mdAndUp' (value) {
      if (!value) {
        this.playerLoading = false
        this.playerZoomingBack = false
      }
    },
    introAutoClickProgress (value) {
      if (value === 1) {
        this.showIntro = false
      }
    },
    async showIntro (value) {
      if (!value) {
        await this.$nextTick()
        this.onSelectedTalentChange()
        delay(this.searchCenteredTalent, 600)
        this.$vuetify.goTo(scrollUpperMargin)
      }
    },
    async talentId () {
      await this.$nextTick()
      this.$root.$emit('scroll-to-saved-position')
      if (this.talentId === -1) {
        delay(this.searchCenteredTalent, 600)
      }
    },
    selectedTalent (value) {
      this.onSelectedTalentChange()
    },
    talentEntered (value) {
      console.log('talent entered', value)
      this.onSelectedTalentChange()
    }
  },
  methods: {
    async onSelectedTalentChange () {
      if (!this.$refs.player) return

      if (this.talentIdInt !== -1 && !this.talentEntered) return
      if (this.talentIdInt === -1 && this.talentEntered) return

      console.log('zooming back')
      // zoom player back to 0
      this.playerZoomingBack = true
      await this.$refs.player.playTo(0)
      this.playerZoomingBack = false

      console.log('zoom back done')
      const format = this.$root.platform.ie11 ? frames.jpglow : window.Modernizr.webp ? frames.webp : frames.jpg
      if (this.talentIdInt === -1) {
        return [format[0]]
      }

      const start = 75 * (this.talentIdInt - 1)
      const end = start + 74
      // set player to load frames
      console.log('setting player frames')
      this.playerFrames = format.slice(start, end)
      // play frames
      console.log('start zoom in')
      await this.$refs.player.playTo(72)
      console.log('zoom in done')
    },
    searchData (domElement) {
      if (!domElement) {
        return false
      }
      if (domElement.dataset && domElement.dataset.talentIndex !== null && domElement.dataset.talentIndex !== undefined) {
        return domElement
      }
      if (domElement.parentNode) {
        return this.searchData(domElement.parentNode)
      }
      return false
    },
    searchCenteredTalent () {
      if (this.$vuetify.breakpoint.mdAndUp) {
        this.mobileTalentCentered = -1
        return
      }
      const elementUnderCenter = document.elementFromPoint(this.viewPort.width / 2, this.viewPort.height / 2)
      const elementFound = this.searchData(elementUnderCenter)
      if (elementFound) {
        this.mobileTalentCentered = parseInt(elementFound.dataset.talentIndex)
      }
    },
    onScroll (e) {
      if (!this.$refs.talentMenu) return

      this.searchCenteredTalent()

      const bounds = this.$refs.talentMenu.getBoundingClientRect()
      let scrollPos = (window.pageYOffset || window.scrollTop) - (window.clientTop || 0)
      if (isNaN(scrollPos)) scrollPos = 0

      const scrollingUp = this.lastScrollPos > scrollPos

      // console.log(this.lastScrollPos, scrollingUp, scrollPos, bounds.height)

      if (scrollPos > bounds.height) {
        document.documentElement.scrollTop = scrollPos - bounds.height
        document.documentElement.pageYOffset = scrollPos - bounds.height
        this.lastScrollPos = scrollPos - bounds.height
      } else if (scrollPos < scrollUpperMargin && scrollingUp) {
        document.documentElement.scrollTop = bounds.height - scrollUpperMargin
        document.documentElement.pageYOffset = bounds.height - scrollUpperMargin
        this.lastScrollPos = bounds.height - scrollUpperMargin
      } else {
        this.lastScrollPos = scrollPos
      }
    }
  },
  computed: {
    talentIdInt () {
      return this.talentId !== '' && this.talentId ? parseInt(this.talentId) : -1
    },
    selectedTalent () {
      return this.talentIdInt !== -1 && this.talentsList ? find(this.talentsList, { order: this.talentIdInt }) : null
    },
    talentsList () {
      return this.Talents ? this.Talents.items : null
    }
  },
  metaInfo () {
    const pageName = 'talents'
    const imageMeta = JSON.parse(this.$root.siteSettings[`${pageName}PageShareImage`].metaData)
    return {
      // home page browser title
      title: this.$root.siteSettings[`${pageName}PageTitle`],
      meta: [
        { vmid: 'description', name: 'description', content: this.$root.siteSettings[`${pageName}PageShareDescription`] },
        { vmid: 'ogtitle', property: 'og:title', itemprop: 'name', content: `${this.$root.siteSettings[`${pageName}PageTitle`]} | ${this.$root.siteSettings.siteTitle}` },
        { vmid: 'ogdescription', property: 'og:description', content: this.$root.siteSettings[`${pageName}PageShareDescription`] },
        { vmid: 'ogimage', property: 'og:image', content: `https://${this.$root.process.env.SERVICE_TARGET_DOMAIN}/media/${this.$root.siteSettings[`${pageName}PageShareImage`].id}` },
        { vmid: 'ogimagetype', property: 'og:image:type', content: this.$root.siteSettings[`${pageName}PageShareImage`].mimeType },
        { vmid: 'ogimageheight', property: 'og:image:width', content: imageMeta['image-width'] },
        { vmid: 'ogimagewidth', property: 'og:image:height', content: imageMeta['image-height'] }
      ]
    }
  }
}
</script>

<style scoped lang="scss">

.new-talents-page {
  /*overflow: hidden;*/
  position: relative;
  height: 100%;
  width: 100%;
}

.player-view {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  transition: all 0.6s $ease-in-out-quad;
  pointer-events: none;
  contain: strict;

  // @include breakpoint('sm-and-down') {
  //   position: relative;
  // }

  &--full {
    width: 70%;
    transform: translate3d(30vw, 0, 0);

    // @include breakpoint('sm-and-down') {
    //   width: 100%;
    //   transform: translate3d(0, 0, 0);
    // }
  }

  &--restricted {
    transform: translate3d(0, 0, 0);
    width: calc(40% + 84px);

    // @include breakpoint('sm-and-down') {
    //   height: 50vh;
    //   right: 0;
    //   width: 100%;
    // }
  }
}

.talent-menu {
  position: absolute;
  top: 0;
  left: 0;
  overflow-x: visible;
  width: 30%;
  will-change: transform;
  backface-visibility: hidden;
  background-color: $c-light-pink;

  @include breakpoint('sm-and-down') {
    background-color: initial;
    width: 100%;
  }

  .current,
  .after {
    background-color: $c-light-pink;
    @include breakpoint('sm-and-down') {
      background-color: initial;
    }
  }

  a {
    margin-left: 50%;
    width: 200%;
    white-space: nowrap;
    padding-top: 40px !important;
    padding-bottom: 40px !important;

    @include breakpoint('lg-and-down') {
      padding-top: 30px !important;
      padding-bottom: 30px !important;
    }

    @include breakpoint('sm-and-down') {
      white-space: break-spaces;
      width: 100%;
      margin-left: 0;
      font-size: 2rem;
      padding-left: 40px;
      padding-right: 40px;
      padding-top: 80px !important;
      padding-bottom: 80px !important;
    }

    span {
      display: block;
      font-family: $heading-font-family;

      .safari & { // stylelint-disable-line
        padding-top: 10px; // fixes https://app.clickup.com/t/7wte3g
      }
    }

    .nationality {
      font-family: $body-font-family;
      font-size: 1rem;
      color: map-get(map-get($material-light, 'text'), 'secondary');
    }
  }

  .after {
    position: absolute;
    top: 100%;
    left: 0;
    width: 100%;
  }
}

.talent-menu-overlay {
  position: fixed;
  top: 0;
  left: 0;
  height: 100%;
  width: 200px;
  pointer-events: none;

  .centered {
    position: absolute;
    top: 50%;
    left: 0;
    transform: translateY(-50%);
  }

  li {
    position: relative;
    list-style: none;
    padding: 0;
    margin: 0;
    color: #000;

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 50%;
      transform: translate3d(-50%, -50%, 0) scale(1);
      background-color: #fff;
      border-radius: 50%;
      width: 5px;
      height: 5px;
      transition: all 0.5s;
      box-shadow: 1px 1px 3px 0 rgba(0, 0, 0, 0.25);
    }

    &:hover {
      a { opacity: 1; }

      &::before {
        background-color: #ffc9c4;
        transform: translate3d(-50%, -50%, 0) scale(2);
      }
    }

    &.active {
      &::before {
        background-color: #ffc9c4;
        transform: translate3d(-50%, -50%, 0) scale(2);
      }
    }
  }

  a {
    pointer-events: all;
    opacity: 0;
    transition: all 0.5s;
    padding-left: 25px;
    padding-top: 10px;
    padding-bottom: 10px;
    display: block;
    color: #fff;
    // background-color: rgba(0, 0, 0, 0.3);

    &.router-link-active {
      color: $c-pink;
    }
  }
}

.image-player {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.talent-description {
  position: absolute;
  width: 58%;
  height: 100%;
  right: 0;
  top: 0;
  will-change: transform;
  backface-visibility: hidden;
  // contain: layout;

  @include breakpoint('sm-and-down') {
    position: relative;
    width: 100%;
  }
}

.fixed-close-button {
  top: 20px;
  left: initial;
  right: calc(58% - 120px);

  @include breakpoint ('sm-and-down') {
    position: absolute;
    right: initial;
    top: calc(50vh + 5px);
    left: 10px;
  }
}

.intro {
  position: fixed;
  top: 0;
  left: 0;
  width: 110%;
  height: 100%;
  background-color: $c-light-pink;
  z-index: 1;
}

.world-map {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 100%;
  height: 80%;
  z-index: -1;
  transform: translate3d(-54%, -50%, 0);
}

.intro-page {
  margin-top: 20vh;
  margin-left: -20vw;

  @include breakpoint('sm-and-down') {
    margin-top: 0;
    margin-left: 0;
  }

  p {
    font-family: $heading-font-family;
    color: #131313;
    font-size: 4rem;
    line-height: 1.1;

    @include breakpoint('sm-and-down') {
      font-size: 2rem;
    }
  }
}

.close-icon {
  width: 12px;
  height: 12px;
  color: map-get($shades, 'black');
  stroke: currentColor;
}

.mobile-preview {
  position: fixed;
  top: 50%;
  left: 40%;
  width: calc(60% - 20px);
  height: 40%;
  transform: translateY(-50%);
}

.player-enter,
.player-leave-to {
  // transform: translate3d(30vw, 0, 0) scale3d(1.3, 1.3, 1.3) !important;

  &.player-view--full {
    transform: translate3d(30vw, 0, 0) scale3d(1.3, 1.3, 1.3);
    @include breakpoint('sm-and-down') {
      transform: translate3d(0, 0, 0)  scale3d(1.3, 1.3, 1.3);
    }
  }

  &.player-view--restricted {
    transform: translate3d(0, 0, 0);
  }
}

.player-enter-active,
.player-leave-active {
  .no-prerenderer & {
    transition: all 2.5s $ease-out-quint !important;

    &.player-view--restricted {
      transition: all 0s $ease-out-quint !important;
    }
  }

}

.talent-description-enter-active,
.talent-description-leave-active,
.talent-menu-enter-active,
.talent-menu-leave-active {
  .no-prerenderer & {
    transition: transform 0.5s $ease-in-out-quad, opacity 0.5s $ease-in-out-quad;
  }
}

.talent-menu-enter,
.talent-menu-leave-to {
  transform: translate3d(-100%, 0, 0);
  opacity: 0;
}

.talent-description-enter,
.talent-description-leave-to {
  transform: translate3d(100%, 0, 0);
  opacity: 0;
}

.background-filler {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: $c-light-pink;
}

</style>
